import { TreeList } from '@ibsys/kit';
import { FC, memo } from 'react';
import { applyNbspTypography } from '@/shared/lib';
import type { NavigationNode } from '../../model';

type Props = {
  node: NavigationNode;
  onNodeSelect?: (nodeId: string) => void;
  selectedNodeId?: string | null;
  /**
   * Множество id всех узлов на пути от корня до активного (включая сам активный).
   * Используется компаратором `React.memo` ниже: если узел и его поддерево
   * не пересекаются с этим путём ни в предыдущем, ни в текущем рендере —
   * ререндер можно пропустить.
   */
  selectedPathSet: Set<string>;
};

const NavigationTreeNodeImpl: FC<Props> = ({ node, onNodeSelect, selectedNodeId, selectedPathSet }) => {
  const hasChildren = node.children && node.children.length > 0;
  const handleClick = () => onNodeSelect?.(node.id);
  const isSelected = selectedNodeId === node.id;
  if (hasChildren) {
    return (
      <TreeList.Group
        value={node.id}
        label={<span className={isSelected ? 'font-bold' : undefined}>{applyNbspTypography(node.name)}</span>}
        onClick={handleClick}
      >
        {node.children!.map((child) => (
          <NavigationTreeNode
            key={child.id}
            node={child}
            onNodeSelect={onNodeSelect}
            selectedNodeId={selectedNodeId}
            selectedPathSet={selectedPathSet}
          />
        ))}
      </TreeList.Group>
    );
  }
  return (
    <TreeList.Item value={node.id} onClick={handleClick}>
      <span className={isSelected ? 'font-bold' : undefined}>{applyNbspTypography(node.name)}</span>
    </TreeList.Item>
  );
};

/**
 * Мемоизированный узел: ререндер только если меняется сам узел/коллбэк
 * или если узел оказался (или перестал быть) на пути от корня до активной секции.
 * При скролле scrollspy меняет `selectedNodeId` десятки раз в секунду — без memo
 * каждое изменение перерисовало бы всё дерево.
 */
export const NavigationTreeNode = memo(NavigationTreeNodeImpl, (prev, next) => {
  if (prev.node !== next.node) return false;
  if (prev.onNodeSelect !== next.onNodeSelect) return false;
  const wasInPath = prev.selectedPathSet.has(prev.node.id);
  const isInPath = next.selectedPathSet.has(next.node.id);
  // Узел не на пути ни в прошлом, ни в текущем рендере — поддерево не задето.
  if (!wasInPath && !isInPath) return true;
  return false;
});
